import React, { useContext } from "react";
import compose from "compose-function";
import moment from "moment";

import {
  addFormatters,
  addHrefToRow,
  addValueFormatter,
  changeDeletedToBoolean,
  generateEntityKeyForLocalStorage,
  getDefaultFilterModel,
  getInitialFilters,
  padValueWith,
  readDataFromLocalStorage,
} from "../../utils/table";
import { GlobalContext } from "../../globalContext";
import { FILTER_TYPES } from "../../dao/types";

import { getDefaultSort } from "../../constants/defaultSorting";

import UserRights from "../../enums/UserRights";
import {
  getSortOrder,
  sortColumns,
  sortModelToString,
  fetchDataWithOptionsAndFilters,
  exportToFile,
} from "../../utils/utils";
import CreateItemButton from "../CreateItemButton";
import { addSearchParams } from "../../utils/dao";
import { ROUTES } from "../../enums/Routes";
import { copyRecord, editRecords, exportRecords } from "../../dao/common";
import { hardDelete, softDelete } from "../../dao/operations";
import { API_ROUTES } from "../../enums/api";
import TableComponent from "../TableComponent";
import { useColumns } from "../../hooks/useColumns";
import useUserPermissionsContext from "../../hooks/useUserPermissionsContext";
import useEntities from "../../hooks/useEntities";
import { useSortModel } from "../../hooks/useSortModel";

const API_ROUTE = API_ROUTES["cable-bundles"];

/**
 * Here we need to transform the row data retrieved,
 * in order to make it compatible with @mui/x-data-grid-pro
 */
const transformRows = (rows) => {
  return rows.reduce((acc, row) => {
    return [
      ...acc,
      compose(
        changeDeletedToBoolean("deleted"),
        addHrefToRow({
          field: "id",
          slug: "bundle",
        })
      )(row),
    ];
  }, []);
};

/**
 * Here we need to transform the column data retrieved,
 * in order to add @mui/x-data-grid-pro formatters
 */
const transformColumns = (columns) => {

    return addFormatters(
      [
        { field: "number", formatter: addValueFormatter("number")(padValueWith(4)) },
        { field: "object_number", formatter: addValueFormatter("object_number")(padValueWith(5)) },
      ],
      columns);

};

export const CableBundlesContainer = ({
  nestedEntity = false,
  parent = {},
}) => {
  const { globalFilters } = useContext(GlobalContext);

  // Get cached local filter model from local storage
  // with a fallback to default field values
  const cachedLocalFilter = readDataFromLocalStorage(
    generateEntityKeyForLocalStorage(FILTER_TYPES.cable_bundle, "filterModel", {
      nestedEntity,
    })
  ) ?? {
    filterModel: null,
    filterJSON: "",
  };

  // destructure fields from cachedLocalFilter
  const {
    filterModel: cachedLocalFilterModel,
    filterJSON: cachedFilterJSONString,
  } = cachedLocalFilter;

  const initialFilters = getInitialFilters({
    cache: {
      localFilterModel: cachedLocalFilterModel,
      filterJSONString: cachedFilterJSONString,
    },
    defaultFilterModel: getDefaultFilterModel("number").filterModel,
  });

  const { columns } = useColumns(FILTER_TYPES.cable_bundle);

  const defaultSortString = getDefaultSort(FILTER_TYPES.cable_bundle);

  const { sortModel, setSortModel } = useSortModel(FILTER_TYPES.cable_bundle, {
    nestedEntity,
    columns,
  });
  const sortModelString =
    sortModelToString(sortModel, columns) || defaultSortString;

  const {
    data,
    loadingState,
    fetchEntitiesData: fetchCableBundlesData,
    setFilterString,
    setOffset,
    limit,
    offset,
    setLimit,
    totalNumberOfRecords,
  } = useEntities("cable-bundles", {
    params: { object_ids: parent.object_id ?? globalFilters?.value ?? null },
    initialFilters: initialFilters.apiFilterString,
    order: sortModelString,
  });

  const fetchRecordsDataWithGlobalFilters = fetchDataWithOptionsAndFilters(
    fetchCableBundlesData
  )({
    limit,
    offset,
    order: sortModelString,
    params: {
      object_ids: parent.object_id ?? globalFilters?.value ?? null,
    },
  });

  const fetchExportData = async (filterString, selectedColumnsString) => {
    const abortController = new AbortController();
    const searchParams = {
      filters: filterString,
      columns: selectedColumnsString,
      object_ids: parent?.object_id || globalFilters?.value,
    };
    const response = await exportRecords(
      API_ROUTE,
      searchParams,
      abortController
    );

    exportToFile(
      `export_${FILTER_TYPES.cable_bundle}_overview_${moment().format(
        "DD-MM-YYYY"
      )}.csv`,
      response
    );

    return "Export done";
  };

  const { isAllowedTo } = useUserPermissionsContext();

  const isAllowedToCreate = isAllowedTo(
    UserRights.CREATE,
    FILTER_TYPES.cable_bundle
  );
  const isAllowedToDelete = isAllowedTo(
    UserRights.SOFT_DELETE,
    FILTER_TYPES.cable_bundle
  );
  const isAllowedToExport = isAllowedTo(UserRights.EXPORT_CSV);
  const isAllowedToMassEdit = isAllowedTo(UserRights.MASS_EDIT_CABLE_BUNDLE);
  const isAllowedToInlineEdit = isAllowedTo(
    UserRights.INLINE_EDIT_CABLE_BUNDLE
  );

  const tableOptions = {};
  tableOptions.hasDelete = isAllowedToDelete;
  tableOptions.hasCopy = isAllowedToCreate;
  tableOptions.hasMassEdit = isAllowedToMassEdit;
  tableOptions.sortOrder = getSortOrder(FILTER_TYPES.cable_bundle);
  tableOptions.hasInlineEdit = isAllowedToInlineEdit;

  const searchParams = new URLSearchParams(
    Object.entries({
      objectId: parent?.object_id || globalFilters?.value,
    }).filter(([, value]) => value)
  ).toString();

  return (
    <>
      <TableComponent
        {...{
          columns: transformColumns(sortColumns(columns)),
          transformColumns,
          loading: loadingState,
          rows: transformRows(data),
          refreshData: fetchRecordsDataWithGlobalFilters,
          renderCreateEntityButton: () => (
            <CreateItemButton
              to={addSearchParams(ROUTES.bundle.create)(searchParams)}
              isDisabled={!isAllowedToCreate}
              entity='bundle'
            />
          ),
          copyEntry: copyRecord,
          editRecords,
          softDeleteEntry: softDelete({
            route: API_ROUTE,
            delete: true,
          }),
          hardDeleteEntry: hardDelete,
          restoreEntry: softDelete({
            route: API_ROUTE,
            delete: false,
          }),
          tableOptions,
          setFilters: setFilterString,
          setOffset,
          offset,
          setLimit,
          sortModel,
          setSortModel,
          numberOfRecords: totalNumberOfRecords,
          isAllowedToExport,
          entity: FILTER_TYPES.cable_bundle,
          nestedEntity,
          initialFilterModel: initialFilters.grid,
          exportRecords: fetchExportData,
        }}
      />
    </>
  );
};
